// ---------------------------------------------------------------------------
// Theme core — ONE stamp: data-theme="light" on <html>, persisted per-device
// at 'cgp:theme'. Absent key = dark, the default the whole team knows; light
// is opt-in. Deliberately NO prefers-color-scheme handling: a register that
// flips mid-shift because the OS schedule ticked over is worse than opt-in.
// The pre-paint snippet in index.html applies the stored value before first
// paint; this module owns every change after that.
// Node tests import this file — every DOM/storage touch is guarded.
// ---------------------------------------------------------------------------
import { useSyncExternalStore } from 'react';

export type Theme = 'dark' | 'light';

export const THEME_STORAGE_KEY = 'cgp:theme';

// The PWA/browser chrome abuts the sidebar, so theme-color tracks the
// sidebar token's value per theme (there is no web manifest to also update).
const META_COLOR: Record<Theme, string> = { dark: '#1B3A2D', light: '#EFE8DA' };

const listeners = new Set<() => void>();

export function getTheme(): Theme {
  if (typeof document === 'undefined') return 'dark';
  // The <html> attribute is the source of truth: it is what is actually
  // rendering, even if storage was cleared or the pre-paint stamp failed.
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
}

export function setTheme(t: Theme): void {
  if (typeof document !== 'undefined') {
    if (t === 'light') document.documentElement.setAttribute('data-theme', 'light');
    else document.documentElement.removeAttribute('data-theme');
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', META_COLOR[t]);
  }
  try {
    localStorage.setItem(THEME_STORAGE_KEY, t);
  } catch {
    /* private mode / no storage — the session still themes, it just won't stick */
  }
  listeners.forEach((fn) => fn());
}

/** Same-tab change feed for the two controls (sidebar footer + Settings).
 *  Cross-tab `storage` events are deliberately out of scope for v1. */
export function subscribeTheme(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Both theme controls render from this, so flipping either updates the other. */
export function useTheme(): Theme {
  return useSyncExternalStore(subscribeTheme, getTheme, () => 'dark');
}
